import { keyframes, styled } from "@mui/material/styles";
import { Box, Typography, Button, Card, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

export const WalletContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
}));

export const WalletText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: theme.typography.body1.fontSize,
  color: theme.palette.text.secondary,
}));

export const BalanceCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[100],
  borderLeft: `5px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[1],
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxWidth: 400,
  cursor: "pointer",
  "&:hover": {
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.grey[200],
  },
}));

export const BalanceAmount = styled(Typography)<{ amount: number }>(({ theme, amount }) => ({
  fontSize: "2rem",
  fontWeight: 600,
  color: amount < 0 ? theme.palette.error.main : theme.palette.primary.main,
}));

export const BalanceCurrency = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  color: theme.palette.text.secondary,
}));

export const BalanceRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const BalanceSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.subtitle2.fontSize,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

export const BalanceItem = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: `${theme.spacing(0.5)} 0`,
}));

export const BalanceLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.primary,
}));

export const BalanceValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 500,
}));

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const TransactionCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  animation: `${fadeSlideUp} 0.4s ease-in-out`,
  backgroundColor: theme.palette.background.paper,
  transition: "box-shadow 0.3s ease, transform 0.3s ease",

  "&:hover": {
    boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-5px)",
  },
}));

export const AmountAndStatusRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(4),
  flexWrap: "wrap",
}));

export const AmountText = styled(Typography)<{ amount: number }>(({ theme, amount }) => ({
  fontWeight: 600,
  color: amount >= 0 ? theme.palette.success.main : theme.palette.error.main,
}));

export const CommentText = styled(Typography)(({ theme }) => ({
  fontStyle: "italic",
  color: theme.palette.grey[600],
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const FiltersBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[1],
}));

export const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: 180,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 6,
  fontSize: theme.typography.body2.fontSize,
  "& .MuiInputBase-root": {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    height: 40,
    fontSize: theme.typography.body2.fontSize,
    backgroundColor: theme.palette.grey[50],
    borderRadius: 6,
    border: `1px solid ${theme.palette.grey[300]}`,
    "&:hover": {
      borderColor: theme.palette.primary.light,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputLabel-root": {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.secondary,
    top: "-4px",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.85)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "20px",
    marginRight: theme.spacing(1),
    color: theme.palette.grey[600],
  },
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2.5),
  fontSize: theme.typography.body2.fontSize,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  borderRadius: 6,
  boxShadow: "none",
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export const StyledSelectField = styled(TextField)(({ theme }) => ({
  minWidth: 220,
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: theme.typography.body2.fontSize,
    backgroundColor: theme.palette.grey[50],
    borderRadius: 6,
    border: `1px solid ${theme.palette.grey[300]}`,
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    "&:hover": {
      borderColor: theme.palette.primary.light,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputLabel-root": {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.secondary,
    top: "-4px",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.85)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "20px",
    color: theme.palette.grey[600],
  },
}));